import { TerrainType } from '../../world/terrain';
import type { UnitTypeId } from '../units/unitDefs';

/**
 * Resource identifiers (master resource set, DECISIONS D26). Energy
 * (Phase 8) and population (Phase 4) are not stockpiled yet.
 */
export type ResourceId = 'wood' | 'scrap' | 'food' | 'energy' | 'population';

/** Resources held in a player's ledger and stockpiled in storage (Phase 3). */
export type StockResource = 'wood' | 'scrap' | 'food';
export const STOCK_RESOURCES: readonly StockResource[] = ['wood', 'scrap', 'food'];

/** An amount of each stock resource (missing = 0). */
export type ResourceAmounts = Readonly<Partial<Record<StockResource, number>>>;

export type BuildingTypeId =
  | 'command_center'
  | 'survivor_housing'
  | 'sawmill'
  | 'farm'
  | 'scrap_yard'
  | 'warehouse'
  | 'wall'
  | 'barracks'
  | 'workshop'
  | 'gate'
  | 'watchtower'
  | 'gun_tower'
  | 'generator';

/** A building that shoots (Phase 8). Uses the Phase 6 damage rules and projectiles. */
export interface TurretSpec {
  /** Tiles from the footprint edge. */
  readonly range: number;
  readonly damage: number;
  readonly damageType: import('../combat/rules').DamageType;
  readonly cooldown: number;
  /** Projectile speed, tiles/s. */
  readonly projectileSpeed: number;
}

/** What a level-2 building gets (Phase 8). Fields left out stay as level 1. */
export interface UpgradeSpec {
  readonly name: string;
  readonly cost: ResourceAmounts;
  readonly seconds: number;
  readonly maxHealth: number;
  readonly armor?: number;
  readonly housing?: number;
  readonly storage?: ResourceAmounts;
  readonly turret?: TurretSpec;
  readonly power?: number;
}

/** A building's output once complete: `amount` of `resource` every `intervalSeconds` of simulation time. */
export interface ProductionRule {
  readonly resource: StockResource;
  readonly amount: number;
  readonly intervalSeconds: number;
}

export type BuildingCategory = 'core' | 'housing' | 'production' | 'logistics' | 'defense' | 'military';

/** Size in tiles along grid X and grid Y. */
export interface Footprint {
  readonly width: number;
  readonly height: number;
}

/** Extra, building-specific placement requirement (in addition to the universal rules). */
export interface NearbyTerrainRule {
  readonly terrain: TerrainType;
  /** Tiles counted within this many tiles (Chebyshev) of the footprint. */
  readonly radius: number;
  readonly minTiles: number;
  /** Player-facing name of what is needed, e.g. "forest tiles". */
  readonly label: string;
}

export interface BuildingDefinition {
  readonly id: BuildingTypeId;
  readonly name: string;
  readonly category: BuildingCategory;
  /** What the building is FOR in the finished game. */
  readonly purpose: string;
  /** The phase in which its function is implemented. Until then it is placed and built but not operational. */
  readonly operationalFromPhase: number;
  readonly footprint: Footprint;
  readonly maxHealth: number;
  /** Flat armour against every hit (Phase 6; walls and towers get some in Phase 8). Default 0. */
  readonly armor?: number;
  /** Seconds of simulation time to construct. 0 = completes instantly. */
  readonly buildTimeSeconds: number;
  /** Construction cost, deducted from the owner's ledger when the placement command is accepted. */
  readonly cost: ResourceAmounts;
  /** Output once complete (none for non-producers). */
  readonly production?: ProductionRule;
  /** Storage capacity this building adds once complete. */
  readonly storage?: ResourceAmounts;
  /** Survivors this building shelters once complete (population capacity). */
  readonly housing?: number;
  /** Worker places; production speed scales with workers / workerSlots. */
  readonly workerSlots?: number;
  /** Unit types this building can train (Phase 5). */
  readonly trains?: readonly UnitTypeId[];
  /** Can the player place it from the construction bar? (The Command Center is a starting structure.) */
  readonly playerBuildable: boolean;
  /** Can the player demolish it? */
  readonly demolishable: boolean;
  /** Blocks unit movement once pathfinding exists. */
  readonly blocksMovement: boolean;
  readonly requiresNearby?: NearbyTerrainRule;
  /** Keyboard shortcut in the construction bar. */
  readonly hotkey?: string;
  /** Gate: blocks everyone except its owner's units (Phase 8). */
  readonly ownerPassable?: boolean;
  /** Shoots enemies in range (Phase 8). */
  readonly turret?: TurretSpec;
  /** Power this building supplies once complete (generators). */
  readonly power?: number;
  /** Power this building needs to work (only works while powered). */
  readonly powerUse?: number;
  /** Optional single upgrade to level 2. */
  readonly upgrade?: UpgradeSpec;
}

const DEFS: readonly BuildingDefinition[] = [
  {
    id: 'command_center',
    name: 'Command Center',
    category: 'core',
    purpose: 'The heart of the colony. Coordinates survivors and holds the starting supplies. If it falls, the colony is lost.',
    operationalFromPhase: 3,
    footprint: { width: 3, height: 3 },
    maxHealth: 1500,
    buildTimeSeconds: 0,
    cost: {},
    storage: { wood: 200, scrap: 150, food: 150 },
    housing: 10,
    playerBuildable: false,
    demolishable: false,
    blocksMovement: true,
  },
  {
    id: 'survivor_housing',
    name: 'Survivor Housing',
    category: 'housing',
    purpose: 'Shelter for survivors. More housing lets the colony grow.',
    operationalFromPhase: 4,
    footprint: { width: 2, height: 2 },
    maxHealth: 300,
    buildTimeSeconds: 20,
    cost: { wood: 25 },
    housing: 8,
    upgrade: { name: 'Fortified Housing', cost: { wood: 30, scrap: 10 }, seconds: 20, maxHealth: 420, housing: 14 },
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    hotkey: '1',
  },
  {
    id: 'sawmill',
    name: 'Sawmill',
    category: 'production',
    purpose: 'Cuts timber from the surrounding forest. Must be built near trees.',
    operationalFromPhase: 3,
    footprint: { width: 2, height: 2 },
    maxHealth: 350,
    buildTimeSeconds: 25,
    cost: { wood: 30 },
    production: { resource: 'wood', amount: 5, intervalSeconds: 10 },
    workerSlots: 3,
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    requiresNearby: { terrain: TerrainType.Forest, radius: 3, minTiles: 4, label: 'forest tiles' },
    hotkey: '2',
  },
  {
    id: 'farm',
    name: 'Farm',
    category: 'production',
    purpose: 'Grows food to feed the colony.',
    operationalFromPhase: 3,
    footprint: { width: 3, height: 3 },
    maxHealth: 250,
    buildTimeSeconds: 20,
    cost: { wood: 20 },
    production: { resource: 'food', amount: 4, intervalSeconds: 10 },
    workerSlots: 3,
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    hotkey: '3',
  },
  {
    id: 'warehouse',
    name: 'Warehouse',
    category: 'logistics',
    purpose: 'Stores resources. Raises how much the colony can stockpile.',
    operationalFromPhase: 3,
    footprint: { width: 2, height: 2 },
    maxHealth: 500,
    buildTimeSeconds: 30,
    cost: { wood: 30, scrap: 10 },
    storage: { wood: 200, scrap: 150, food: 100 },
    upgrade: { name: 'Depot', cost: { wood: 30, scrap: 20 }, seconds: 25, maxHealth: 650, storage: { wood: 300, scrap: 225, food: 150 } },
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    hotkey: '4',
  },
  {
    id: 'wall',
    name: 'Basic Wall',
    category: 'defense',
    purpose: 'A barricade of scrap and timber. Blocks the infected and funnels them into kill zones.',
    operationalFromPhase: 8,
    footprint: { width: 1, height: 1 },
    maxHealth: 200,
    armor: 4,
    buildTimeSeconds: 4,
    cost: { wood: 2, scrap: 2 },
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    hotkey: '5',
    upgrade: { name: 'Reinforced Wall', cost: { scrap: 6 }, seconds: 8, maxHealth: 450, armor: 7 },
  },
  {
    id: 'scrap_yard',
    name: 'Scrap Yard',
    category: 'production',
    purpose: 'Strips metal from the ruins of the old world. Must be built next to ruins.',
    operationalFromPhase: 3,
    footprint: { width: 2, height: 2 },
    maxHealth: 350,
    buildTimeSeconds: 25,
    cost: { wood: 35 },
    production: { resource: 'scrap', amount: 3, intervalSeconds: 12 },
    workerSlots: 3,
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    requiresNearby: { terrain: TerrainType.Ruins, radius: 2, minTiles: 3, label: 'ruin tiles' },
    hotkey: '6',
  },
  {
    id: 'barracks',
    name: 'Military Barracks',
    category: 'military',
    purpose: 'Trains Rangers and Heavies from your survivors.',
    operationalFromPhase: 5,
    footprint: { width: 3, height: 2 },
    maxHealth: 700,
    buildTimeSeconds: 30,
    cost: { wood: 60, scrap: 20 },
    trains: ['ranger', 'heavy'],
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    hotkey: '7',
  },
  {
    id: 'workshop',
    name: 'Workshop',
    category: 'military',
    purpose: 'Trains Engineers, who repair your buildings.',
    operationalFromPhase: 5,
    footprint: { width: 2, height: 2 },
    maxHealth: 450,
    buildTimeSeconds: 25,
    cost: { wood: 40, scrap: 20 },
    trains: ['engineer'],
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    hotkey: '8',
  },
  {
    id: 'gate',
    name: 'Gate',
    category: 'defense',
    purpose: 'A wall your own units can walk through. Everyone else has to break it.',
    operationalFromPhase: 8,
    footprint: { width: 1, height: 1 },
    maxHealth: 300,
    armor: 4,
    buildTimeSeconds: 8,
    cost: { wood: 6, scrap: 6 },
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    ownerPassable: true,
    hotkey: '0',
    upgrade: { name: 'Reinforced Gate', cost: { scrap: 10 }, seconds: 10, maxHealth: 600, armor: 7 },
  },
  {
    id: 'watchtower',
    name: 'Watchtower',
    category: 'defense',
    purpose: 'A rifle nest on stilts. Shoots the nearest enemy in range.',
    operationalFromPhase: 8,
    footprint: { width: 2, height: 2 },
    maxHealth: 400,
    armor: 2,
    buildTimeSeconds: 25,
    cost: { wood: 40, scrap: 20 },
    turret: { range: 6, damage: 10, damageType: 'bullet', cooldown: 1.2, projectileSpeed: 16 },
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    hotkey: '9',
    upgrade: {
      name: 'Sniper Nest',
      cost: { wood: 30, scrap: 30 },
      seconds: 20,
      maxHealth: 550,
      armor: 3,
      turret: { range: 7, damage: 14, damageType: 'bullet', cooldown: 1.2, projectileSpeed: 18 },
    },
  },
  {
    id: 'gun_tower',
    name: 'Gun Tower',
    category: 'defense',
    purpose: 'A heavy machine-gun tower. Hits hard, but needs 5 power from a Generator.',
    operationalFromPhase: 8,
    footprint: { width: 2, height: 2 },
    maxHealth: 600,
    armor: 4,
    buildTimeSeconds: 35,
    cost: { wood: 40, scrap: 60 },
    turret: { range: 7, damage: 22, damageType: 'bullet', cooldown: 1.5, projectileSpeed: 20 },
    powerUse: 5,
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
  },
  {
    id: 'generator',
    name: 'Generator',
    category: 'logistics',
    purpose: 'Burns scrap fuel for electricity. Each one powers two Gun Towers.',
    operationalFromPhase: 8,
    footprint: { width: 2, height: 2 },
    maxHealth: 350,
    buildTimeSeconds: 30,
    cost: { wood: 30, scrap: 40 },
    power: 10,
    playerBuildable: true,
    demolishable: true,
    blocksMovement: true,
    upgrade: { name: 'Twin Generator', cost: { scrap: 40 }, seconds: 25, maxHealth: 450, power: 20 },
  },
];

/** Buildings reach at most this level (one upgrade). */
export const MAX_BUILDING_LEVEL = 2;

/** A building's effective stats at its level (level 1 = the definition; level 2 = with its upgrade). */
export interface LevelStats {
  readonly name: string;
  readonly maxHealth: number;
  readonly armor: number;
  readonly housing: number;
  readonly storage: ResourceAmounts | undefined;
  readonly turret: TurretSpec | undefined;
  readonly power: number;
}

export function statsAt(type: BuildingTypeId, level: number): LevelStats {
  const d = buildingDef(type);
  const u = level >= 2 ? d.upgrade : undefined;
  return {
    name: u?.name ?? d.name,
    maxHealth: u?.maxHealth ?? d.maxHealth,
    armor: u?.armor ?? d.armor ?? 0,
    housing: u?.housing ?? d.housing ?? 0,
    storage: u?.storage ?? d.storage,
    turret: u?.turret ?? d.turret,
    power: u?.power ?? d.power ?? 0,
  };
}

/** Repair price (Phase 8): scrap per point of health restored. */
export const REPAIR = { scrapPerHealth: 0.1 } as const;

/**
 * Economy constants (DECISIONS D45). Rates live on the building definitions;
 * these are the colony-wide rules. All amounts are whole numbers.
 */
export const ECONOMY = {
  /** Each player's ledger at match start (before any override). */
  startingResources: { wood: 150, scrap: 60, food: 100 } as Readonly<Record<StockResource, number>>,
  /** How often survivors eat (simulation seconds). */
  upkeepIntervalSeconds: 20,
  /** Food each survivor eats per upkeep cycle (Phase 4: upkeep is per survivor). */
  foodPerSurvivor: 1,
  /** Share of the cost returned when demolishing a COMPLETE building (rounded down). */
  refundComplete: 0.5,
  /** Share returned when demolishing a building still under construction (cancelling it). */
  refundUnderConstruction: 1,
} as const;

/** Population constants (DECISIONS D51). */
export const POPULATION = {
  /** Survivors each colony starts with. */
  startingSurvivors: 8,
  /** Hard cap per player, whatever the housing. */
  maxPopulation: 200,
  /** A survivor joins after this many seconds of good conditions (room, food, no shortage). */
  growthIntervalSeconds: 30,
  /** Food needed in store before newcomers are taken in. */
  growthMinFood: 10,
} as const;

export const BUILDING_DEFINITIONS: ReadonlyMap<BuildingTypeId, BuildingDefinition> = new Map(DEFS.map((d) => [d.id, d]));

/** Buildings the player can place, in construction-bar order. */
export const PLAYER_BUILDABLE: readonly BuildingDefinition[] = DEFS.filter((d) => d.playerBuildable);

export function buildingDef(id: BuildingTypeId): BuildingDefinition {
  const d = BUILDING_DEFINITIONS.get(id);
  if (!d) throw new Error(`Unknown building type "${id}"`);
  return d;
}

export function isBuildingTypeId(v: unknown): v is BuildingTypeId {
  return typeof v === 'string' && BUILDING_DEFINITIONS.has(v as BuildingTypeId);
}

export const RESOURCE_LABEL: Record<ResourceId, string> = {
  wood: 'Wood',
  scrap: 'Scrap',
  food: 'Food',
  energy: 'Energy',
  population: 'Population',
};

export function formatCost(cost: Readonly<Partial<Record<ResourceId, number>>>): string {
  const parts = (Object.keys(cost) as ResourceId[]).filter((k) => (cost[k] ?? 0) > 0).map((k) => `${cost[k]} ${RESOURCE_LABEL[k].toLowerCase()}`);
  return parts.length ? parts.join(', ') : 'none';
}
